import { useCallback } from 'react';
import { useWritingScore } from './use-writing-score';
import { UserProfile } from '@/lib/firebase/users';
import {
  StructuredEssayData,
  StructuredWritingData,
  WritingTaskType,
  isEssayData,
  createStructuredEssayData,
} from '@/types/writing';
import {
  QuestionType,
  QuestionNote,
  Task1Type,
  VisualType,
} from '@/types/task-bank';

interface UseScoreManagerProps {
  userId: string | undefined;
  currentEssayId: string | null;
  userProfile: UserProfile | null;
  isProUser: boolean;
  onScoreSuccess?: () => void;
}

export function useEssayScoreManager({
  userId,
  currentEssayId,
  isProUser,
  onScoreSuccess = () => {},
}: UseScoreManagerProps) {
  const handleScoreFetched = useCallback(() => {
    if (typeof onScoreSuccess === 'function') {
      onScoreSuccess();
    }
  }, [onScoreSuccess]);

  const handleHistoryCreated = useCallback(() => {}, []);

  const scoreState = useWritingScore({
    userId,
    currentEssayId,
    isProUser,
    onScoreFetched: handleScoreFetched,
    onHistoryCreated: handleHistoryCreated,
  });

  const fetchScore = useCallback(
    (
      prompt: string,
      essayData: StructuredWritingData,
      taskType: WritingTaskType = 'task2',
      questionType?: QuestionType | '',
      questionNote?: QuestionNote | '',
      task1Type?: Task1Type,
      visualType?: VisualType
    ) => {
      // Convert StructuredWritingData to StructuredEssayData for the underlying score function
      let convertedData: StructuredEssayData;

      if (isEssayData(essayData)) {
        // Already in the correct format
        convertedData = essayData;
      } else {
        // Convert report data to essay data format for scoring using helper function
        convertedData = createStructuredEssayData(
          essayData.introduction,
          essayData.bodyParagraphs,
          essayData.overview // Map overview to conclusion for scoring
        );
      }

      scoreState.fetchNewScore(
        prompt,
        convertedData,
        taskType,
        questionType,
        questionNote,
        task1Type,
        visualType
      );
    },
    [scoreState]
  );

  const checkScoringRequirements = useCallback(
    (data: StructuredWritingData, taskType: WritingTaskType = 'task2') => {
      // Convert to StructuredEssayData format for the underlying function
      let convertedData: StructuredEssayData;

      if (isEssayData(data)) {
        convertedData = data;
      } else {
        convertedData = createStructuredEssayData(
          data.introduction,
          data.bodyParagraphs,
          data.overview
        );
      }

      return scoreState.checkScoringRequirements(convertedData, taskType);
    },
    [scoreState]
  );

  return {
    score: scoreState.score,
    scoreError: scoreState.scoreError,
    isScoring: scoreState.isScoring,
    hasHistory: scoreState.hasHistory,
    isCheckingHistory: scoreState.isCheckingHistory,

    showMinimalChangeDialog: scoreState.showMinimalChangeDialog,
    setShowMinimalChangeDialog: scoreState.setShowMinimalChangeDialog,

    fetchScore,
    clearScore: scoreState.clearScore,

    checkScoringRequirements,
  };
}
